/**
 * Warning-only print preflight for the Export stage.
 *
 * Walks every spread's elements through the shared print-safety checks
 * (text safe area, image dead-zone edges) and phrases the results for the
 * author. Purely informational: warnings NEVER block an export — the
 * author always keeps the final say.
 */
import type { FormatPreset, Spread } from '@historyjki/shared';
import {
  SAFE_MARGIN_MM,
  SPREAD_TEXT_PANEL_PAD,
  checkElementPlacement,
  classifySharpness,
  effectiveImageDpi,
} from '@historyjki/shared';
import { loadImageDims } from './imageDims';
import { parseSpreadElements, spreadLabel } from './spreadGeometry';

export interface PreflightWarning {
  /** Stable key for React lists. */
  id: string;
  message: string;
}

/** Human phrasing for one element's placement warning, prefixed by the
 *  spread label ("Cover", "Spread 3", "Back"). */
export function collectPlacementWarnings(
  spreads: Spread[],
  formatPreset: FormatPreset | null | undefined,
): PreflightWarning[] {
  const warnings: PreflightWarning[] = [];
  let interiorIndex = -1;

  for (const spread of spreads) {
    if (spread.kind === 'spread') interiorIndex += 1;
    const { label } = spreadLabel(spread, interiorIndex);
    const elements = parseSpreadElements(spread.elements);

    for (const el of elements) {
      const w = checkElementPlacement(
        el,
        formatPreset,
        spread.kind,
        SPREAD_TEXT_PANEL_PAD,
      );
      if (!w) continue;
      if (w.kind === 'image-dead-zone') {
        const edges = w.edges?.join(', ') ?? '';
        warnings.push({
          id: `${spread.id}:${el.id}`,
          message:
            `${label}: an image stops just short of the page edge (${edges}) — ` +
            'a thin white strip may show after trimming. Extend it past the ' +
            'edge or move it clearly inside.',
        });
      } else if (w.gutter) {
        warnings.push({
          id: `${spread.id}:${el.id}`,
          message:
            `${label}: a text block sits in the gutter fold — the binding can ` +
            'swallow it. Move it fully onto one page.',
        });
      } else {
        warnings.push({
          id: `${spread.id}:${el.id}`,
          message:
            `${label}: a text block sits within ${SAFE_MARGIN_MM} mm of the ` +
            'trimmed edge and may be cut off. Move it toward the middle of the page.',
        });
      }
    }
  }

  return warnings;
}

/**
 * Print-sharpness preflight: flag every placed image whose pixel count
 * can't carry its printed size (the same effective-resolution math as the
 * compose editor's badge). Async because it needs each image's natural
 * dimensions — loads go through the shared cache, so images the editor
 * already showed resolve instantly. Warning-only, like all preflight.
 */
export async function collectSharpnessWarnings(
  spreads: Spread[],
  formatPreset: FormatPreset | null | undefined,
): Promise<PreflightWarning[]> {
  interface Placed {
    spreadId: string;
    label: string;
    kind: Spread['kind'];
    el: ReturnType<typeof parseSpreadElements>[number] & { type: 'image' };
  }
  const placed: Placed[] = [];
  let interiorIndex = -1;
  for (const spread of spreads) {
    if (spread.kind === 'spread') interiorIndex += 1;
    const { label } = spreadLabel(spread, interiorIndex);
    for (const el of parseSpreadElements(spread.elements)) {
      if (el.type !== 'image') continue;
      placed.push({ spreadId: spread.id, label, kind: spread.kind, el });
    }
  }

  // Dimensions load in parallel (deduped by the shared cache) — a cold
  // Export stage would otherwise decode dozens of images sequentially.
  const dims = await Promise.all(placed.map((p) => loadImageDims(p.el.r2_key)));

  const warnings: PreflightWarning[] = [];
  placed.forEach((p, i) => {
    const d = dims[i];
    if (!d) return; // unknown pixels — don't guess a warning
    const level = classifySharpness(
      effectiveImageDpi(p.el, d.w, d.h, formatPreset, p.kind),
    );
    if (level === 'sharp') return;
    warnings.push({
      id: `${p.spreadId}:${p.el.id}:sharpness`,
      message:
        `${p.label}: an image will print ${level === 'blurry' ? 'visibly blurry' : 'slightly soft'} ` +
        'at its placed size. Shrink it on the spread (its toolbar has a ' +
        'one-tap fix), or generate a take at a bigger size preset.',
    });
  });

  return warnings;
}
